import { useQuery } from '@tanstack/react-query'
import { Building2, ChevronDown } from 'lucide-react'

import { businessDayQueryOptions } from '@/api/reference-api'
import { BusinessDate } from '@/components/app-shell/business-date'
import { useBranchSelection } from '@/components/app-shell/use-branch-selection'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { branchLabel } from '@/lib/branch'
import { formatShortDate } from '@/lib/datetime'

/**
 * Branch and business date on a narrow top bar.
 *
 * The two qualify every figure on every screen below them, so they cannot simply be dropped when
 * the bar runs out of room: a balance means one thing in Kaneshie on an open day and something
 * else in Madina after close. What is given up on a 375px screen is having them permanently
 * spelled out, not having them — the trigger keeps the branch code and the date visible, and the
 * menu holds the full reading and the switch.
 */
export function HeaderContextMenu() {
  const { branches, selectedBranchId, currentBranch, canSwitch, selectBranch } = useBranchSelection()
  const { data: businessDay } = useQuery(businessDayQueryOptions(selectedBranchId))

  const dayClosed = businessDay?.status !== undefined && businessDay.status !== 'Open'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex min-h-11 min-w-0 items-center gap-1.5 rounded-control px-2 text-left transition-colors hover:bg-surface-hover"
          aria-label="Branch and business date"
        >
          <Building2 className="size-4 shrink-0 text-muted" aria-hidden />
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate text-xs font-semibold text-heading">
              {branchLabel(currentBranch)}
            </span>
            <span className="numeric flex items-center gap-1 text-2xs text-muted tabular-nums">
              {businessDay?.businessDate ? formatShortDate(businessDay.businessDate) : 'No day open'}
              {dayClosed ? (
                <span className="size-1.5 shrink-0 rounded-full bg-warning-fill" aria-hidden />
              ) : null}
            </span>
          </span>
          <ChevronDown className="size-3.5 shrink-0 text-muted" aria-hidden />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="min-w-64">
        <DropdownMenuLabel>Business date</DropdownMenuLabel>
        <div className="px-2 pb-2">
          <BusinessDate layout="stacked" />
        </div>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>{canSwitch ? 'Operating branch' : 'Branch'}</DropdownMenuLabel>

        {canSwitch ? (
          <DropdownMenuRadioGroup value={selectedBranchId ?? undefined} onValueChange={selectBranch}>
            {branches.map((branch) => (
              <DropdownMenuRadioItem key={branch.id} value={branch.id}>
                <span className="font-mono text-xs text-muted">{branchLabel(branch)}</span>
                {branch.name ? <span className="truncate">{branch.name}</span> : null}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        ) : (
          <div className="px-2 pb-2">
            <p className="text-sm font-medium text-heading">{branchLabel(currentBranch)}</p>
            {currentBranch ? (
              <p className="font-mono text-xs text-muted">{currentBranch.code}</p>
            ) : null}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
